import Link from 'next/link'
import { Icon, type IconName } from './icon'

/** Seção do guia aprofundado (conteúdo de SEO abaixo da ferramenta). */
export function GuiaSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-9">
      <h2 className="mb-3.5 text-[22px] font-semibold tracking-[-0.02em] text-ink">{title}</h2>
      {children}
    </section>
  )
}

/** Parágrafo do guia, com a medida/altura de linha do design system. */
export function GuiaP({ children }: { children: React.ReactNode }) {
  return <p className="mb-3.5 text-[15.5px] leading-[1.7] text-ink-soft">{children}</p>
}

/** Caixa de fórmula em mono (ex.: "(quer ganhar + custos) ÷ horas = valor da hora"). */
export function FormulaBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-4 border border-line bg-background p-4 font-mono text-sm leading-relaxed text-ink">
      {children}
    </div>
  )
}

/** Item de "erros comuns" — ícone em quadrado petróleo + título + texto. */
export function Bullet({
  icon,
  title,
  children,
}: {
  icon: IconName
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="mb-4 flex items-start gap-3.5">
      <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center bg-brand-tint">
        <Icon name={icon} size={17} className="text-brand" />
      </div>
      <div>
        <div className="mb-0.5 text-[15.5px] font-medium text-ink">{title}</div>
        <div className="text-[14.5px] leading-relaxed text-ink-soft">{children}</div>
      </div>
    </div>
  )
}

/** Card de calculadora relacionada (links internos / circulação + SEO). */
export function RelCard({
  icon,
  title,
  desc,
  href,
  badge,
}: {
  icon: IconName
  title: string
  desc: string
  href: string
  /** Selo opcional (ex.: "Popular") no canto superior direito. */
  badge?: string
}) {
  return (
    <Link
      href={href}
      className="group relative block rounded-xl border border-line bg-surface p-[18px] shadow-soft transition-all hover:-translate-y-0.5 hover:border-brand hover:shadow-lift"
    >
      {badge && (
        <span className="absolute right-3 top-3 rounded-full bg-coral-soft px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.04em] text-coral-ink">
          {badge}
        </span>
      )}
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-[11px] bg-brand-tint">
        <Icon name={icon} size={18} className="text-brand" />
      </div>
      <div className="mb-1 text-[15px] font-semibold text-ink">{title}</div>
      <div className="mb-2.5 text-[13px] leading-snug text-ink-mute">{desc}</div>
      <div className="flex items-center gap-1.5 text-[12.5px] font-medium text-brand">
        Abrir <Icon name="arrow" size={13} className="transition-transform group-hover:translate-x-0.5" />
      </div>
    </Link>
  )
}
